import {
  Button,
  Flex,
  Text,
  Stack,
  TabPanel,
  UnorderedList,
  ListItem,
  useToast,
  Box,
} from "@chakra-ui/react";
import { useTenant } from "modules/tenant/providers/tenant-provider";
import { useContext, useState, useCallback, Component, ErrorInfo, ReactNode } from "react";

const MAX_FILTER_FIELDS = 70; // Maximum number of fields allowed in a filter

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Uncaught error:", error, errorInfo);
  }

  public render() {
    if (this.state.hasError) {
      return (
        <Box p={4} bg="red.50" borderRadius="md">
          <Text color="red.500" fontSize="14px">
            Something went wrong. Please try again.
          </Text>
        </Box>
      );
    }

    return this.props.children;
  }
}

const SavedFilters = ({ setFilters }: { setFilters: (f: any) => void }) => {
  const { user } = useTenant();
  const [loadingFilterId, setLoadingFilterId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();

  const cleanFilterData = useCallback((filter: any) => {
    try {
      if (!filter || !filter.filters) {
        throw new Error("Invalid filter: Missing filter data");
      }

      // Parse the filters if they're a string
      let parsedFilters;
      try {
        parsedFilters =
          typeof filter.filters === "string" ? JSON.parse(filter.filters) : filter.filters;
      } catch (parseError) {
        throw new Error("Invalid filter format: Could not parse filter data");
      }

      // Basic validation
      if (!parsedFilters || typeof parsedFilters !== "object") {
        throw new Error("Invalid filter format: Filters must be an object");
      }

      // Remove any undefined, null, or empty string values
      const cleanedFilters = Object.entries(parsedFilters).reduce((acc, [key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          // If the value is an object, clean it recursively
          if (typeof value === "object") {
            const cleanedValue = Object.entries(value).reduce((objAcc, [objKey, objValue]) => {
              if (objValue !== undefined && objValue !== null && objValue !== "") {
                objAcc[objKey] = objValue;
              }
              return objAcc;
            }, {} as Record<string, any>);

            // Only add the object if it has properties
            if (Object.keys(cleanedValue).length > 0) {
              acc[key] = cleanedValue;
            }
          } else {
            acc[key] = value;
          }
        }
        return acc;
      }, {} as Record<string, any>);

      // Check if we have too many fields
      const fieldCount = Object.keys(cleanedFilters).length;
      if (fieldCount > MAX_FILTER_FIELDS) {
        throw new Error(
          `Filter contains too many fields (${fieldCount}). Maximum allowed is ${MAX_FILTER_FIELDS}.`
        );
      }

      // Check if we have any valid filters
      if (Object.keys(cleanedFilters).length === 0) {
        throw new Error("Invalid filter: No valid filter criteria found");
      }

      return cleanedFilters;
    } catch (error) {
      console.error("Error cleaning filter data:", error);
      throw error;
    }
  }, []);

  const applyFilter = useCallback(
    async (filter: any) => {
      try {
        setError(null);
        setLoadingFilterId(filter.id);

        // Clean and validate the filter data
        const cleanedFilters = cleanFilterData(filter);

        // Apply the filters
        setFilters(cleanedFilters);

        toast({
          title: "Filter applied",
          description: `Successfully applied filter: ${filter.title}`,
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } catch (error) {
        console.error("Error applying filter:", error);

        // More specific error handling
        let errorMessage = "There was an error applying this filter.";
        if (error instanceof Error) {
          errorMessage = error.message;
          setError(error.message);
        } else if (typeof error === "object" && error !== null) {
          // Try to extract a meaningful message from the error object
          errorMessage = (error as any).message || JSON.stringify(error);
          setError(errorMessage);
        }

        toast({
          title: "Error applying filter",
          description: errorMessage,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setLoadingFilterId(null);
      }
    },
    [cleanFilterData, setFilters, toast]
  );

  // Add null check for user.saved_filters
  const savedFilters = user?.saved_filters || [];

  if (error) {
    return (
      <TabPanel p="0px">
        <Text color="red.500" textAlign="center" w="100%" fontSize="14px" mt="20px">
          {error}
        </Text>
      </TabPanel>
    );
  }

  return (
    <ErrorBoundary>
      <TabPanel p="0px">
        {savedFilters.length === 0 ? (
          <Text textAlign="center" w="100%" fontSize="14px" mt="20px">
            You don't have any saved filters.
          </Text>
        ) : (
          <UnorderedList p="0px" m="0px" listStyleType="none">
            {savedFilters.map((f: any) => (
              <ListItem key={f.id} borderBottomWidth="1px" borderBottomColor="gray.100" py="20px">
                <Flex alignItems="center">
                  <Stack flex="1" mr="30px" spacing="0px">
                    <Text fontWeight="500" fontSize="14px" color="#595959" mb="0px">
                      {f.title}
                    </Text>
                    <Text fontSize="14px" mt="4px">
                      {f.description}
                    </Text>
                  </Stack>
                  <Button
                    variant="secondary"
                    width="84px"
                    height="34px"
                    onClick={() => applyFilter(f)}
                    isLoading={loadingFilterId === f.id}
                  >
                    Use
                  </Button>
                </Flex>
              </ListItem>
            ))}
          </UnorderedList>
        )}
      </TabPanel>
    </ErrorBoundary>
  );
};

export default SavedFilters;
